import { AlertCircle, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { AdHocOrganizationWageSet } from "../../hooks/useOrganizationWageSetsForAdHocEntry";
import { formatWageSummary } from "@/features/organization-wage-sets/utils/wage-fields";

interface AdHocOrganizationWageSetFieldProps {
  organizationWageSets: AdHocOrganizationWageSet[];
  isLoading: boolean;
  organizationName: string;
  value: string | undefined;
  onChange: (organizationWageSetId: string) => void;
  disabled: boolean;
  error?: string;
}

export function AdHocOrganizationWageSetField({
  organizationWageSets,
  isLoading,
  organizationName,
  value,
  onChange,
  disabled,
  error,
}: AdHocOrganizationWageSetFieldProps) {
  const selectedOrgWageSet = organizationWageSets.find(
    (orgWageSet) => orgWageSet.organizationWageSetId === value,
  );

  return (
    <div className="space-y-2">
      <Label htmlFor="organizationWageSetId">
        Wage set <span className="text-red-500">*</span>
      </Label>
      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading wage sets...
        </div>
      )}
      {!isLoading && organizationWageSets.length === 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            You don't have a wage set at {organizationName} for this date. Ask
            them to assign one.
          </AlertDescription>
        </Alert>
      )}
      {!isLoading && organizationWageSets.length > 0 && (
        <Select value={value} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger
            id="organizationWageSetId"
            className="h-auto py-2 [&>span]:line-clamp-none"
          >
            <SelectValue placeholder="Select a wage set">
              {selectedOrgWageSet && (
                <span className="text-left">
                  {formatWageSummary(selectedOrgWageSet.wages)}
                </span>
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {organizationWageSets.map((orgWageSet) => (
              <SelectItem
                key={orgWageSet.organizationWageSetId}
                value={orgWageSet.organizationWageSetId}
              >
                {/* Only the wages: a wage set's name may be internal to the organization. */}
                <span className="text-left">
                  {formatWageSummary(orgWageSet.wages)}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}
